<i18n>
{
	"en": {
		"viaLocation": "Via Location",
		"addViaLocation": "Add Via Location",
		"viaLocationDateTimeTitle": "Planned Date and Time",
		"address": "Address",
		"cancel": "Cancel",
		"moveViaUp": "Move up",
		"moveViaDown": "Move down"
	},
	"no": {
		"viaLocation": "Via Sted",
		"addViaLocation": "Legg til via sted",
		"viaLocationDateTimeTitle": "Planlagt dato og tid",
		"address": "Adresse",
		"cancel": "Avbryt",
		"moveViaUp": "Flytt opp",
		"moveViaDown": "Flytt ned"
	}
}
</i18n>

<template>
	<div class="via-locations">
		<t-button
			:value="$t('addViaLocation')"
			iconBefore="add"
			iconType="bp"
			width="auto"
			@click="onAddViaLocation"
		/>
		<template
			v-for="(item, index) in viaLocations"
		>
			<div
				:key="item.id || index"
			>
				<t-button
					:value="item.address.name || `${$t('viaLocation')} ${index + 1}`"
					:appearance="currentViaLocationIndex === index && showViaEdit ? 'primary' : 'default'"
					width="auto"
					@click="editVialocation(index)"
				/>
			</div>
		</template>
		<t-dialog
			:isShown="showViaEdit"
			focusFirstOnShow
			alwaysRememberFocus
			:title="`${$t('viaLocation')} ${currentViaLocation ? currentViaLocation.viaNumber + 1 : ''}`"
			@close="onDialogClose"
		>
			<div
				v-if="currentViaLocation"
				class="edit-via-location"
			>
				<TripAddressInput
					class="via-location-in-edit"
					:title="$t('address')"
					:value="currentViaLocation.address"
					focusNextOnSelected
					@change="updateViaLocation({update: {address: $event}, index: currentViaLocationIndex})"
				/>
				<t-date-time-input
					:title="$t('viaLocationDateTimeTitle')"
					:dateTimeObject="currentViaLocation.dateTimeObject"
					@date-input="setViaLocationDate({ index: currentViaLocationIndex, dateValue: $event, hasTime: null })"
					@time-input="setViaLocationDate({ index: currentViaLocationIndex, timeValue: $event, hasTime: Boolean($event) })"
				/>
			</div>

			<template
				#footer
			>
				<div style="width: 100%; display: flex; justify-content: space-between">
					<div style="display:flex">
						<t-button
							v-if="viaLocationIsNew"
							:value="$t('cancel')"
							@click="onRemoveViaLocation"
						/>
						<t-icon-button
							v-else
							intent="danger"
							appearance="primary"
							icon="trash"
							type="bp"
							@click="onRemoveViaLocation"
						/>
					</div>
					<div style="display:flex">
						<t-button
							:style="{visibility: currentViaLocationIndex === 0 ? 'hidden' : '' }"
							:value="$t('moveViaUp')"
							iconBefore="arrow-up"
							iconType="bp"
							width="auto"
							appearance="minimal"
							@click="moveViaUp"
						/>
						<t-button
							:style="{visibility: currentViaLocationIndex === viaLocations.length - 1 ? 'hidden' : '' }"
							:value="$t('moveViaDown')"
							iconBefore="arrow-down"
							iconType="bp"
							width="auto"
							appearance="minimal"
							@click="moveViaDown"
						/>
					</div>
					<div style="display:flex">
						<t-button
							value="Ok"
							appearance="primary"
							@click="onDialogClose"
						/>
					</div>
				</div>
			</template>
		</t-dialog>
	</div>

</template>

<script>
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';

import {
	delay,
} from '@/core';
import { mapActions, mapMutations } from './store/newTrip.store.js';

export default {
	name: 'ViaLocations',
	components: {
		TripAddressInput,
	},
	props: {
		viaLocations: Array,
	},
	data() {
		return {
			currentViaLocationIndex: -1,
			showViaEdit: false,
			viaLocationIsNew: false,
			addPending: false,
		};
	},
	computed: {
		currentViaLocation() {
			return (this.currentViaLocationIndex >= 0) ? this.viaLocations[this.currentViaLocationIndex] : false;
		},
	},

	methods: {
		...mapActions(['addViaLocation', 'setViaLocationDate']),
		...mapMutations([
			'removeViaLocation',
			'updateViaLocation',
			'swapViaLocationOrder',
		]),

		onAddViaLocation() {
			if (this.addPending) return;
			this.addPending = true;
			this.currentViaLocationIndex = this.viaLocations.length;
			this.addViaLocation();
			this.viaLocationIsNew = true;
			this.showViaEdit = true;
		},
		editVialocation(index) {
			this.currentViaLocationIndex = index;
			this.showViaEdit = true;
		},
		onRemoveViaLocation() {
			this.removeViaLocation(this.currentViaLocationIndex);
			this.closeViaLocation();
		},

		async closeViaLocation() {
			this.showViaEdit = false;
			// t-dialog uses 150 ms to close, so we need to keep the dialog content consistent meanwhile
			await delay(150);
			this.viaLocationIsNew = false;
			this.currentViaLocationIndex = -1;
		},
		onDialogClose() {
			this.addPending = false;
			if (this.currentViaLocation && Object.keys(this.currentViaLocation.address).length <= 1) {
				this.onRemoveViaLocation();
			} else {
				this.closeViaLocation();
			}
		},
		moveViaUp() {
			this.swapViaLocationOrder({
				viaNumberBefore: this.currentViaLocationIndex,
				viaNumberAfter: this.currentViaLocationIndex - 1,
			});
			this.currentViaLocationIndex -= 1;
		},
		moveViaDown() {
			this.swapViaLocationOrder({
				viaNumberBefore: this.currentViaLocationIndex,
				viaNumberAfter: this.currentViaLocationIndex + 1
			});
			this.currentViaLocationIndex += 1;
		},
	},
};
</script>

<style lang="scss" scoped>
.via-locations {
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-column: span 3;
	grid-template-columns: repeat(auto-fill, minmax(160px, max-content));

	& button {
		width: 100% !important;
	}
}

.edit-via-location {
	display: grid;
	column-gap: 20px;
	row-gap: 20px;
	grid-template-rows: 1fr max-content;
}
</style>
